import { TabStyles } from '@tableau/widgets';
import React from 'react';
import { Link } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import { FieldDataType, FieldRole, FieldRoleCategory } from '../../../../../gen-src/queries';
import {
  divWithStyle,
  SiteUrlsContext
} from '../../../data-catalog-shared';
import {
  FieldDataTypeMappings,
  FieldRoleCategoryMappings,
  FieldRoleMappings,
} from '../field/functions/FieldEnumPropertyMappings';
import { getRowHeight, rowHeight } from '../sharedDisplayGrid/LinksDisplayGrid';
import { ColumnAndFieldDetailsDescriptionValue, ColumnAndFieldDetailsLabel, ColumnAndFieldDetailsValue, descriptionMaxHeight } from '../style/ColumnAndFieldDetailsStyles';
import {
  FieldInfo,
  ParameterInfo,
  TableauExpressionFormatter,
} from '../tableauExpression/components/TableauExpressionFormatter';
import { ColumnsReferencingFieldQueryGrid } from './ColumnsReferencingFieldQueryGrid';
import { FieldsReferencedByFieldQueryGrid } from './FieldsReferencedByFieldQueryGrid';

export const ColumnAndFieldDetailsProperty = divWithStyle('ColumnAndFieldDetailsProperty', {
  display: 'table-row',
});

export const ColumnAndFieldDetailsPlaceholder = divWithStyle('ColumnAndFieldDetailsPlaceholder', {
  fontStyle: 'italic',
  color: TabStyles.Typography.Color.Secondary,
});

export const fieldsWrapperMaxRows = 4.5;

export const FieldsWrapper = divWithStyle('ColumnFieldsWrapper', {
  width: '100%',
  lineHeight: `${rowHeight}px`,
  verticalAlign: 'top',
  display: 'table-cell',
  padding: TabStyles.Spacing.DefaultSpace,
  $nest: {
    '> div': {
      height: 'inherit',
      maxHeight: rowHeight * fieldsWrapperMaxRows,
      overflow: 'auto',
    }
  }
});

function yesOrNo(b: boolean): string {
  return b ? Messages.FieldDetails_Yes() : Messages.FieldDetails_No();
}

export function FieldPropertyDescription(props: { labelFunc: () => string, description: string | null }): JSX.Element {
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{props.labelFunc()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsDescriptionValue>
        <div style={{ maxHeight: descriptionMaxHeight }}>{props.description
          ? props.description
          : <ColumnAndFieldDetailsPlaceholder>{Messages.NoDescriptionAvailable()}</ColumnAndFieldDetailsPlaceholder>}
        </div>
      </ColumnAndFieldDetailsDescriptionValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyFolderName(props: { folderName: string | null }): JSX.Element | null {
  if (props.folderName == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_Folder()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.folderName}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyIsHidden(props: { isHidden: boolean | null }): JSX.Element | null {
  if (props.isHidden == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_Hidden()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{yesOrNo(props.isHidden)}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyDataCategory(props: { dataCategory: FieldRoleCategory | null }): JSX.Element | null {
  if (props.dataCategory == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_DataCategory()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{FieldRoleCategoryMappings[props.dataCategory]()}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyDataType(props: { dataType: FieldDataType | null }): JSX.Element | null {
  if (props.dataType == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_DataType()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{FieldDataTypeMappings[props.dataType]()}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyRole(props: { role: FieldRole | null }): JSX.Element | null {
  if (props.role == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_Role()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{FieldRoleMappings[props.role]()}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyAggregation(props: { aggregation: string | null }): JSX.Element | null {
  if (props.aggregation == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_Aggregation()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.aggregation}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyAggregationParam(props: { aggregationParam: string | null }): JSX.Element | null {
  if (props.aggregationParam == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_AggregationParam()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.aggregationParam}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyDefaultFormat(props: { defaultFormat: string | null }): JSX.Element | null {
  if (props.defaultFormat == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_DefaultFormat()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.defaultFormat}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertySemanticRole(props: { semanticRole: string | null }): JSX.Element | null {
  if (props.semanticRole == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_SemanticRole()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.semanticRole}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyFormula(props: {
  formula: string | null,
  fields: ReadonlyArray<FieldInfo> | null,
  parameters: ReadonlyArray<ParameterInfo> | null,
  makeFieldUrl: (fieldId: string) => string,
}): JSX.Element | null {
  if (props.formula == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_Formula()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>
        <TableauExpressionFormatter
          expression={props.formula}
          fields={props.fields}
          parameters={props.parameters}
          makeFieldUrl={props.makeFieldUrl}
        />
      </ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyBinSize(props: { binSize: string | null }): JSX.Element | null {
  if (props.binSize == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_BinSize()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.binSize}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyHasUserReference(props: { hasUserReference: boolean | null }): JSX.Element | null {
  if (props.hasUserReference == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_HasUserReference()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{yesOrNo(props.hasUserReference)}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyIsAutoGenerated(props: { isAutoGenerated: boolean | null }): JSX.Element | null {
  if (props.isAutoGenerated == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_Autogenerated()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{yesOrNo(props.isAutoGenerated)}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyCombinationType(props: { combinationType: string | null }): JSX.Element | null {
  if (props.combinationType == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_CombinationType()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.combinationType}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyDelimiter(props: { delimiter: string | null }): JSX.Element | null {
  if (props.delimiter == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_Delimiter()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{props.delimiter}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldPropertyHasOther(props: { hasOther: boolean | null }): JSX.Element | null {
  if (props.hasOther == null) {
    return null;
  }
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.FieldDetails_HasOther()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>{yesOrNo(props.hasOther)}</ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function FieldsReferencedByField(props: { fieldId: string, rowCount: number | null }): JSX.Element {
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.ReferencedFields()}</ColumnAndFieldDetailsLabel>
      <FieldsWrapper style={{ height: getRowHeight(props.rowCount, fieldsWrapperMaxRows) }}>
        <FieldsReferencedByFieldQueryGrid
          fieldId={props.fieldId}
          rowCount={props.rowCount}
        />
      </FieldsWrapper>
    </ColumnAndFieldDetailsProperty>
  );
}

export function ConnectedField(props: {
  fieldId: string,
  fieldName: string | null,
  datasourceVizPortalUrlId: string | null,
  datasourceName: string | null,
}): JSX.Element | null {
  if (props.datasourceVizPortalUrlId == null || props.datasourceName == null || props.fieldName == null) {
    return null;
  }
  const datasourceVizPortalUrlId = props.datasourceVizPortalUrlId;
  const datasourceName = props.datasourceName;
  const linkNameWithParent = Messages.LinkNameWithParent({ name: props.fieldName, parentName: datasourceName });
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.ConnectedField()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>
        <SiteUrlsContext.Consumer>
          {urls =>
            <Link to={urls.datasourceLineageFieldPath(datasourceVizPortalUrlId, props.fieldId)}>
              {linkNameWithParent}
            </Link>
          }
        </SiteUrlsContext.Consumer>
      </ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}


export function ConnectedDatasourceField(props: {
  fieldId: string,
  fieldName: string | null,
  datasourceVizPortalUrlId: string | null,
}): JSX.Element | null {
  if (props.datasourceVizPortalUrlId == null || props.fieldName == null) {
    return null;
  }
  const datasourceVizPortalUrlId = props.datasourceVizPortalUrlId;
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.ConnectedDatasourceField()}</ColumnAndFieldDetailsLabel>
      <ColumnAndFieldDetailsValue>
        <SiteUrlsContext.Consumer>
          {urls =>
            <Link to={urls.datasourceLineageFieldPath(datasourceVizPortalUrlId, props.fieldId)}>
              {props.fieldName}
            </Link>
          }
        </SiteUrlsContext.Consumer>
      </ColumnAndFieldDetailsValue>
    </ColumnAndFieldDetailsProperty>
  );
}

export function ColumnsReferencingField(props: { fieldId: string, rowCount: number | null }): JSX.Element {
  return (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.ConnectedColumns()}</ColumnAndFieldDetailsLabel>
      <FieldsWrapper style={{ height: getRowHeight(props.rowCount, fieldsWrapperMaxRows) }}>
        <ColumnsReferencingFieldQueryGrid
          fieldId={props.fieldId}
          rowCount={props.rowCount}
        />
      </FieldsWrapper>
    </ColumnAndFieldDetailsProperty>
  );
}
